import { Box, Stack, Typography } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import dayjs from "dayjs";
import { AppButton, FormSection } from "../../../../components/ui";
import { ReferralCaseProgressViewModel } from "../../../../api";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import "./CaseStatusSummary.scss";

export type CaseStatusField = {
  label: string;
  value: string | null;
};

export function buildCaseStatusFields(
  progress: ReferralCaseProgressViewModel | null | undefined,
  formatDate: (d?: string | null) => string | null
): CaseStatusField[] {
  return [
    {
      label: "Retainer signed",
      value: formatDate(progress?.retainerSignedDate),
    },
    {
      label: "PAGA notice filed",
      value: formatDate(progress?.pagaNoticeFiledDate),
    },
    {
      label: "Complaint filed",
      value: formatDate(progress?.complaintFiledDate),
    },
    {
      label: "Complaint filed place",
      value: progress?.complaintCourt?.trim() || null,
    },
    {
      label: "Case No",
      value: progress?.caseNumber?.trim() || null,
    },
    {
      label: "Mediation scheduled for",
      value: formatDate(progress?.mediationScheduledDate),
    },
    {
      label: "Compelled to arbitration",
      value: formatDate(progress?.compelledToArbitrationDate),
    },
  ];
}

/** One-line label for tables: latest milestone and/or case number */
export function getCompactCaseStatusLabel(
  progress: ReferralCaseProgressViewModel | null | undefined
): string {
  if (!progress) {
    return "—";
  }

  const milestones: { label: string; date?: string | null }[] = [
    { label: "Compelled to arbitration", date: progress.compelledToArbitrationDate },
    { label: "Mediation scheduled", date: progress.mediationScheduledDate },
    { label: "Complaint filed", date: progress.complaintFiledDate },
    { label: "PAGA notice filed", date: progress.pagaNoticeFiledDate },
    { label: "Retainer signed", date: progress.retainerSignedDate },
  ];

  const latest = milestones.find((m) => m.date);
  const caseNo = progress.caseNumber?.trim();

  if (latest && caseNo) {
    return `${latest.label} · ${caseNo}`;
  }
  if (latest) {
    return latest.label;
  }
  if (caseNo) {
    return `Case ${caseNo}`;
  }
  if (progress.complaintCourt?.trim()) {
    return progress.complaintCourt.trim();
  }
  if (progress.isDraft) {
    return "Draft";
  }
  return "Not set";
}

type CaseStatusSummaryProps = {
  progress: ReferralCaseProgressViewModel | null | undefined;
  leadId?: number | null;
  canEdit?: boolean;
  onEdit?: () => void;
  compact?: boolean;
  className?: string;
};

export default function CaseStatusSummary({
  progress,
  leadId,
  canEdit,
  onEdit,
  compact,
  className,
}: CaseStatusSummaryProps) {
  const formatDate = (d?: string | null) =>
    d ? dayjs(d).format("M/D/YYYY") : null;

  const fields = buildCaseStatusFields(progress, formatDate);

  const content = (
    <Stack spacing={0} className="case-status-summary__grid">
      {fields.map((field) => (
        <Box key={field.label} className="case-status-summary__row">
          <Typography component="span" className="case-status-summary__label">
            {field.label}
          </Typography>
          <Typography
            component="span"
            className={`case-status-summary__value${
              field.value ? "" : " case-status-summary__value--empty"
            }`}
          >
            {field.value ?? "Not set"}
          </Typography>
        </Box>
      ))}
      {progress?.isDraft && (
        <Typography className="case-status-summary__draft">Draft — not yet submitted</Typography>
      )}
    </Stack>
  );

  if (compact) {
    return (
      <Box className={`case-status-summary case-status-summary--compact ${className ?? ""}`}>
        {content}
        {canEdit && onEdit && (
          <AppButton
            className="case-status-summary__edit"
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<EditOutlinedIcon />}
            onClick={onEdit}
          >
            Update case status
          </AppButton>
        )}
      </Box>
    );
  }

  return (
    <FormSection
      className={`case-status-summary ${className ?? ""}`}
      title="Case status"
      subtitle={
        canEdit
          ? "Track litigation milestones for this lead."
          : "Litigation milestones for this lead."
      }
      icon={<AssignmentOutlinedIcon />}
      defaultExpanded
    >
      {content}
      {canEdit && onEdit && (
        <AppButton
          className="case-status-summary__edit"
          variant="contained"
          color="primary"
          fullWidth
          startIcon={<EditOutlinedIcon />}
          onClick={onEdit}
        >
          Update case status
        </AppButton>
      )}
    </FormSection>
  );
}
